import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import '../styles/services.scss';

import CountUp from './micro/CountUp'
import ShinyText from './micro/Shinytext';
import Orb from './micro/Orb';
import CollaborationVisual from './micro/CollaborationVisual';
import codeIcon from '../assets/svg/code.svg';
import seoIcon from '../assets/svg/seo.svg';
import toolIcon from '../assets/svg/tool.svg';
import checkIcon from '../assets/svg/check.svg';

gsap.registerPlugin(ScrollTrigger);

const Services = () => {
  const { t, i18n } = useTranslation();
  const heroRef = useRef(null);
  const advantagesRef = useRef(null);
  const extensionRef = useRef(null);

  // Refresh ScrollTrigger when language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      // Short timeout to allow DOM to update with new text
      setTimeout(() => {
        ScrollTrigger.refresh();
      }, 100);
    };

    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Hero section reveal (Why Us title)
      if (heroRef.current) {
        gsap.fromTo(
          heroRef.current.querySelectorAll('.scroll-reveal-item'),
          { opacity: 0, y: 50 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            stagger: 0.15,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: heroRef.current,
              start: 'top 80%',
              toggleActions: 'play none none none'
            }
          }
        );
      }

      // Advantages section reveal
      if (advantagesRef.current) {
        // Icons
        gsap.fromTo(
          advantagesRef.current.querySelectorAll('.services-advantages__icon'),
          { opacity: 0, scale: 0.8, y: 30 },
          {
            opacity: 1,
            scale: 1,
            y: 0,
            duration: 0.6,
            stagger: 0.1,
            ease: 'back.out(1.7)',
            scrollTrigger: {
              trigger: advantagesRef.current.querySelector('.services-advantages__grid'),
              start: 'top 80%',
              toggleActions: 'play none none none'
            }
          }
        );

        // Grid text items
        gsap.fromTo(
          advantagesRef.current.querySelectorAll('.services-advantages__grid .scroll-reveal-item'),
          { opacity: 0, y: 40 },
          {
            opacity: 1,
            y: 0,
            duration: 0.7,
            stagger: 0.1,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: advantagesRef.current.querySelector('.services-advantages__grid'),
              start: 'top 75%',
              toggleActions: 'play none none none'
            }
          }
        );

        // Bottom section text
        gsap.fromTo(
          advantagesRef.current.querySelectorAll('.services-advantages__bottom .scroll-reveal-item'),
          { opacity: 0, y: 40 },
          {
            opacity: 1,
            y: 0,
            duration: 0.7,
            stagger: 0.12,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: advantagesRef.current.querySelector('.services-advantages__bottom'),
              start: 'top 80%',
              toggleActions: 'play none none none'
            }
          }
        );

        // Visual circle
        gsap.fromTo(
          advantagesRef.current.querySelector('.services-advantages__visual'),
          { opacity: 0, scale: 0.9, y: 30 },
          {
            opacity: 1,
            scale: 1,
            y: 0,
            duration: 0.8,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: advantagesRef.current.querySelector('.services-advantages__bottom'),
              start: 'top 80%',
              toggleActions: 'play none none none'
            }
          }
        );
      }

      // Extension section reveal
      if (extensionRef.current) {
        // Icons
        gsap.fromTo(
          extensionRef.current.querySelectorAll('.services-extension__icon'),
          { opacity: 0, scale: 0.8, y: 30 },
          {
            opacity: 1,
            scale: 1,
            y: 0,
            duration: 0.6,
            stagger: 0.1,
            ease: 'back.out(1.7)',
            scrollTrigger: {
              trigger: extensionRef.current.querySelector('.services-extension__grid'),
              start: 'top 80%',
              toggleActions: 'play none none none'
            }
          }
        );

        // Grid text items
        gsap.fromTo(
          extensionRef.current.querySelectorAll('.services-extension__grid .scroll-reveal-item'),
          { opacity: 0, y: 40 },
          {
            opacity: 1,
            y: 0,
            duration: 0.7,
            stagger: 0.1,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: extensionRef.current.querySelector('.services-extension__grid'),
              start: 'top 75%',
              toggleActions: 'play none none none'
            }
          }
        );

        // Support sphere
        gsap.fromTo(
          extensionRef.current.querySelector('.services-extension__support-visual'),
          { opacity: 0, scale: 0.8, y: 40 },
          {
            opacity: 1,
            scale: 1,
            y: 0,
            duration: 0.9,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: extensionRef.current.querySelector('.services-extension__support'),
              start: 'top 80%',
              toggleActions: 'play none none none'
            }
          }
        );

        // Support text
        gsap.fromTo(
          extensionRef.current.querySelectorAll('.services-extension__support .scroll-reveal-item'),
          { opacity: 0, y: 40 },
          {
            opacity: 1,
            y: 0,
            duration: 0.7,
            stagger: 0.12,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: extensionRef.current.querySelector('.services-extension__support'),
              start: 'top 80%',
              toggleActions: 'play none none none'
            }
          }
        );
      }
    });

    return () => ctx.revert();
  }, []);

  return (
    <>
      {/* Landing Hero */}
      <section className="services-landing">
        {/* Orb background */}
        <div className="services-landing__orb-bg">
          <Orb 
            hue={0} 
            hoverIntensity={0} 
            rotateOnHover={false} 
            forceHoverState={false} 
          />
        </div>

        {/* Central content */}
        <div className="services-landing__center">
          <div className="services-landing__core">
            <span className="services-landing__eyebrow">{t('services.landing.eyebrow')}</span>
            <h1>{t('services.landing.title')}</h1>
            <p className="services-landing__tagline">{t('services.landing.tagline')}</p>
          </div>
        </div>

        {/* Stats bar at bottom */}
        <div className="services-landing__stats-bar">
          {/* 1. Satisfaction */}
          <div className="services-landing__stat">
            <span className="services-landing__stat-number">
            <CountUp
              from={0}
              to={100}
              separator=","
              direction="up"
              duration={1}
              className="services-landing__stat-number"
            />
            %</span>
            <ShinyText text={t('services.stats.satisfaction')} speed={3} className="services-landing__stat-label" />
          </div>
          <div className="services-landing__stat-divider" />
          {/* 2. Sales (Moved here to sit next to Satisfaction on mobile) */}
          <div className="services-landing__stat">
          <span className="services-landing__stat-number">
              <CountUp
                from={1}
                to={1}
                separator=""
                direction="up"
                duration={1}
              />
              -
              <CountUp
                from={0}
                to={4}
                separator=""
                direction="up"
                duration={1}
              />
            </span>
            <ShinyText text={t('services.stats.weeks')} speed={3} className="services-landing__stat-label" />
          </div>
          <div className="services-landing__stat-divider" />
          {/* 3. Weeks (Moved to end to span full width on bottom) */}
          <div className="services-landing__stat">
            <span className="services-landing__stat-number">
                <CountUp
                  from={0}
                  to={35}
                  separator=","
                  direction="up"
                  duration={1}
                />
                %
              </span>
              <ShinyText text={t('services.stats.sales')} speed={3} className="services-landing__stat-label" />
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="services-landing__scroll">
          <span>{t('services.landing.scroll')}</span>
          <div className="services-landing__scroll-line" />
        </div>
      </section>

      {/* Hero Section */}
      <section className="services-hero" ref={heroRef}>
        <div className="section-shell">
          <span className="services-hero__eyebrow scroll-reveal-item">{t('services.hero.eyebrow')}</span>
          <h1 className="scroll-reveal-item">{t('services.hero.title')}</h1>
        </div>
      </section>

      {/* Advantages Section */}
      <section className="services-advantages" ref={advantagesRef}>
        <div className="section-shell">
          <div className="services-advantages__grid">
            <div className="services-advantages__item">
              <div className="services-advantages__icon">
                <img src={codeIcon} alt="" />
              </div>
              <h3 className="scroll-reveal-item">{t('services.advantages.tech_title')}</h3>
              <p className="services-advantages__subtitle scroll-reveal-item">
                {t('services.advantages.tech_sub')}
              </p>
              <p className="services-advantages__description scroll-reveal-item">
                {t('services.advantages.tech_desc')}
              </p>
            </div>

            <div className="services-advantages__divider" />

            <div className="services-advantages__item">
              <div className="services-advantages__icon">
                <img src={seoIcon} alt="" />
              </div>
              <h3 className="scroll-reveal-item">{t('services.advantages.perf_title')}</h3>
              <p className="services-advantages__subtitle scroll-reveal-item">
                {t('services.advantages.perf_sub')}
              </p>
              <p className="services-advantages__description scroll-reveal-item">
                {t('services.advantages.perf_desc')}
              </p>
            </div>
          </div>

          <div className="services-advantages__divider services-advantages__divider--horizontal" />

          <div className="services-advantages__bottom">
            <div className="services-advantages__bottom-left">
              <h3 className="scroll-reveal-item">{t('services.advantages.collab_title')}</h3>
              <p className="services-advantages__description scroll-reveal-item">
                {t('services.advantages.collab_desc')}
              </p>
            </div>

            <div className="services-advantages__visual">
              <CollaborationVisual />
            </div>
          </div>
        </div>
      </section>

      <div className="services-advantages__divider services-advantages__divider--horizontal" />
      {/* Why Us Extension */}
      <section className="services-extension" ref={extensionRef}>
        <div className="section-shell">
          <div className="services-extension__grid">
            <div className="services-extension__item">
              <div className="services-extension__icon">
                <img src={toolIcon} alt="" />
              </div>
              <h3 className="scroll-reveal-item">{t('services.extension.tools_title')}</h3>
              <p className="services-extension__description scroll-reveal-item">
                {t('services.extension.tools_desc')}
              </p>
            </div>

            <div className="services-extension__divider" />

            <div className="services-extension__item">
              <div className="services-extension__icon">
                <img src={checkIcon} alt="" className="services-extension__icon--check" />
              </div>
              <h3 className="scroll-reveal-item">{t('services.extension.cms_title')}</h3>
              <p className="services-extension__description scroll-reveal-item">
                {t('services.extension.cms_desc')}
              </p>
            </div>
          </div>

          <div className="services-extension__divider services-extension__divider--horizontal" />

          <div className="services-extension__support">
            <div className="services-extension__support-visual">
              <div className="services-extension__support-rings">
                <div className="services-extension__support-ring services-extension__support-ring--1" />
                <div className="services-extension__support-ring services-extension__support-ring--2" />
                <div className="services-extension__support-ring services-extension__support-ring--3" />
              </div>
              <div className="services-extension__support-sphere">
                <span className="services-extension__support-text">24/7</span>
              </div>
            </div>
            <h3 className="scroll-reveal-item">{t('services.extension.support_title')}</h3>
            <p className="services-extension__support-description scroll-reveal-item">
              {t('services.extension.support_desc')}
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

export default Services;
